import { useState } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { Modal, Button, Alert, FormField, TextArea } from "@a2z/ui";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  /** Display label for this policy (e.g. "Shipping policy") */
  policyLabel: string;
  initialContentAr: string;
  initialContentEn: string;
  /** When true, a note is shown that the content was pre-filled from the system template */
  isPrefilledWithTemplate?: boolean;
  /** Called with the edited values; should throw on failure so the sheet stays open */
  onSave: (contentAr: string, contentEn: string) => Promise<void>;
};

export function PolicyEditSheet({
  isOpen,
  onClose,
  policyLabel,
  initialContentAr,
  initialContentEn,
  isPrefilledWithTemplate = false,
  onSave,
}: Props) {
  const t = useTranslations();
  const [contentAr, setContentAr] = useState(initialContentAr);
  const [contentEn, setContentEn] = useState(initialContentEn);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Reset local state each time the sheet opens with new content —
  // render-time previous-value comparison instead of a setState-in-effect.
  const [prevReset, setPrevReset] = useState({
    isOpen,
    initialContentAr,
    initialContentEn,
  });
  if (
    prevReset.isOpen !== isOpen ||
    prevReset.initialContentAr !== initialContentAr ||
    prevReset.initialContentEn !== initialContentEn
  ) {
    setPrevReset({ isOpen, initialContentAr, initialContentEn });
    if (isOpen) {
      setContentAr(initialContentAr);
      setContentEn(initialContentEn);
      setError(null);
      setIsSaving(false);
    }
  }

  const handleSave = async () => {
    setError(null);
    setIsSaving(true);
    try {
      await onSave(contentAr, contentEn);
    } catch (err) {
      setError(err instanceof Error ? err.message : t(keys.errors.generic));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      title={policyLabel}
      footerActions={
        <>
          <Button
            variant="outline"
            size="md"
            onClick={onClose}
            disabled={isSaving}
          >
            {t(keys.common.cancel)}
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={() => void handleSave()}
            disabled={isSaving}
          >
            {isSaving ? t(keys.common.loading) : t(keys.common.save)}
          </Button>
        </>
      }
    >
      {error ? (
        <Alert message={error} helperText={t(keys.errors.recovery)} />
      ) : null}

      {isPrefilledWithTemplate ? (
        <p className="rounded-lg bg-[color:var(--primary-50)] px-3 py-2.5 text-xs text-[color:var(--primary-700)]">
          {t(keys.seller.storePoliciesTemplateNote)}
        </p>
      ) : null}

      <FormField
        label={t(keys.seller.policyContentArLabel)}
        name="contentAr"
        required
      >
        <TextArea
          name="contentAr"
          value={contentAr}
          onChange={(e) => setContentAr(e.target.value)}
        />
      </FormField>

      <FormField
        label={t(keys.seller.policyContentEnLabel)}
        name="contentEn"
        required
      >
        <TextArea
          name="contentEn"
          value={contentEn}
          onChange={(e) => setContentEn(e.target.value)}
        />
      </FormField>
    </Modal>
  );
}
